import BasicList from 'core/modules/components/lists/components/BasicList/BasicList';
import AssessmentItem from 'core/modules/components/lists/assessments/AssessmentItem/AssessmentItem';
import { stripHtml } from 'core/utils/utils';
import React from 'react';

/**
 * AssessmentsList - Lista de evaluaciones/tareas del alumno. Envuelve el genérico v2
 * `BasicList` (semántica `<ul>/<li>` accesible) y renderiza cada assessment dentro de un
 * `<button>` accesible (focusable + activable por teclado), en vez del antiguo `<div onClick>`
 * sin `role`/`tabIndex`. Mismo patrón de accesibilidad que `TaskList`.
 */

// Solo se leen estos 3 campos aquí; el resto de props las consume `AssessmentItem`
// (JS sin tipar) vía spread, cubiertas por el index signature `Record<string, unknown>`.
export type Assessment = Readonly<{
  lesson_item_guid?: string;
  title?: string;
  lessonName?: string;
}> &
  Readonly<Record<string, unknown>>;

type AssessmentsListProps = Readonly<{
  assessments?: Assessment[];
  onClick?: (assessment: Assessment) => void;
}>;

type AssessmentListItemProps = Readonly<{
  assessment: Assessment;
  onClick: (assessment: Assessment) => void;
}>;

function getAssessmentLabel(assessment: Assessment): string | undefined {
  return (
    [assessment.lessonName, assessment.title]
      .map((value) => stripHtml(value ?? ''))
      .filter(Boolean)
      .join(' - ') || undefined
  );
}

function AssessmentListItem({ assessment, onClick }: AssessmentListItemProps): JSX.Element {
  function handleClick(): void {
    onClick(assessment);
  }

  return (
    <button
      aria-label={getAssessmentLabel(assessment)}
      className="button-accessibility-only lesson-task-list__task lesson-task-list__task-button"
      onClick={handleClick}
      type="button"
    >
      <AssessmentItem {...assessment} />
    </button>
  );
}

function AssessmentsList({ assessments = [], onClick = () => {} }: AssessmentsListProps): JSX.Element {
  return (
    <div className="assessments-list">
      <BasicList
        items={assessments.map((assessment) => (
          <AssessmentListItem key={assessment.lesson_item_guid} assessment={assessment} onClick={onClick} />
        ))}
      />
    </div>
  );
}

export default AssessmentsList;
